import { useEffect, useMemo, useState } from 'react'
import { Field, FieldMetaState, useForm, useFormState } from 'react-final-form'
import {
  DistributionTypeValue,
  TestingMethodValue,
} from '@phrasee/phrasee-typings/typings/project/project-configuration.types'
import cx from 'classnames'

import { CepIntegration, PayloadCepIntegration } from 'common/api'
import useListAccountsIntegrationQuery from 'common/api/queries/useListAccountsCepIntegrationQuery'
import { getIntegrationName } from 'common/cep/cepUtils'
import Button from 'common/components/button'
import FormItem from 'common/components/formItem'
import Input from 'common/components/input'
import PasswordInput from 'common/components/PasswordInput'
import RadioGroup from 'common/components/radioGroup'
import SingleSelect from 'common/components/singleSelect'
import TimeZoneSelect from 'common/components/timeZoneSelect'
import FormCardTitle from 'common/components/title'
import Widget from 'common/components/Widget'
import WidgetHeader from 'common/components/WidgetHeader'
import { useAppSelector } from 'common/hooks/redux'
import { CloseSmall } from 'common/icons'
import { DistributionChannel } from 'common/interfaces/projects'

import { CepTemplateFormatted } from '../../../api/queries/useGetCepIntegrationTemplatesQuery'

import {
  validateDistributionChannel,
  validateDistributionType,
  validateIntegrationCredentials,
  validateName,
  validateTestingMethod,
} from './validation'

type Props = {
  className?: string
  cepTemplate: CepTemplateFormatted
  integrationToUpdate: CepIntegration | undefined
  onClose: () => void
  onSubmit: (payload: PayloadCepIntegration) => void
}

const messageTypeMap = {
  alwayson: 'Triggered',
  broadcast: 'Broadcast',
}

const distributionChannelMap = {
  email: 'Email',
  email_in_body: 'Email in Body',
  push_notification: 'Push Notification',
  sms: 'SMS',
  social_ad: 'Social Ad',
  web_and_app: 'Web and App',
  in_app_message: 'In-App Message',
  content_card: 'Content Card',
  web_push: 'Web Push',
}

const CreateCepIntegrationWidget = ({
  className,
  onClose,
  cepTemplate,
  integrationToUpdate,
  onSubmit,
}: Props) => {
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const formState = useFormState()
  const isEditing = !!integrationToUpdate
  const { hasValidationErrors, dirty } = formState
  const form = useForm()
  const { change: formChange } = form
  const { distributionChannel, testingMethod, distributionType } =
    formState.values
  const [isDistributionChannelSelected, setIsDistributionChannelSelected] =
    useState(false)
  const distributionChannels = cepTemplate.distributionChannel?.map(
    (value) => value.name
  )
  const selectedDistributionChannel = cepTemplate.distributionChannel?.find(
    (item) => item.name === distributionChannel
  )
  const { data: accountsIntegration } =
    useListAccountsIntegrationQuery(accountId)

  const filteredAccountsIntegration = useMemo(() => {
    if (isEditing) {
      return accountsIntegration?.filter(
        (integration) => integration._id !== integrationToUpdate?._id
      )
    }
  }, [isEditing, accountsIntegration, integrationToUpdate])

  useEffect(() => {
    const updates = {
      distributionChannel: distributionChannel,
      testingMethod: testingMethod,
      messageType: testingMethod ? distributionType : undefined,
    }

    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        formChange(key, value)
      }
    })
  }, [formChange, distributionChannel, testingMethod, distributionType])

  useEffect(() => {
    if (integrationToUpdate) {
      form.initialize({
        name: integrationToUpdate.name,
        distributionChannel: integrationToUpdate.distribution_channel,
        testingMethod: integrationToUpdate.testing_methodology,
        distributionType: integrationToUpdate.message_type,
        integrationCredentials: integrationToUpdate.credentials,
      })
      setIsDistributionChannelSelected(true)
    }
  }, [integrationToUpdate, form])

  const getFieldError = (meta: FieldMetaState<any>): string | undefined => {
    if (meta.modified && (meta.error || meta.submitError)) {
      return meta.error || meta.submitError.message
    }
    return undefined
  }

  return (
    <div className="w-125">
      <Widget type="basic" className={cx('bg-gold-50 p-4', className)}>
        <div className="flex items-center justify-between">
          <WidgetHeader
            title={`${getIntegrationName(
              cepTemplate.cepName
            )} integration settings`}
            className="text-3xl font-serif"
          />
          <Button
            onClick={onClose}
            aria-label="Close"
            className="-top-2 -right-4"
          >
            <CloseSmall isDefaultColor={false} size={8} />
          </Button>
        </div>

        <div className="mb-4 text-gray-700">
          Fill in your details and credentials and select 'ADD' to link account.
        </div>
        <form
          id="create-cep-integration-form"
          data-testid="create-cep-integration-form"
          onSubmit={() => {
            onClose()
            onSubmit({
              ...(formState.values as PayloadCepIntegration),
              cep_name: cepTemplate.cepName,
              trackingMetrics: selectedDistributionChannel?.trackingMetrics!,
            })
          }}
        >
          <div>
            <FormCardTitle title="Details" />
            <Field<string>
              name="name"
              validate={(val) =>
                validateName(
                  val,
                  isEditing ? filteredAccountsIntegration : accountsIntegration
                )
              }
            >
              {({ input, meta }) => (
                <FormItem
                  label="Enter name"
                  htmlFor="name"
                  className="mt-4"
                  error={getFieldError(meta)}
                >
                  <Input
                    data-id="integration-name"
                    data-testid="integration-name"
                    type="text"
                    variant="default"
                    id="name"
                    name="name"
                    placeholder="Name of integration should be specific to your use-case"
                    value={input.value}
                    onChange={input.onChange}
                    onBlur={input.onBlur}
                  />
                  <div className="text-3">Mention which channel it's for.</div>
                </FormItem>
              )}
            </Field>

            <Field<DistributionChannel>
              name="distributionChannel"
              validate={validateDistributionChannel}
            >
              {({ input, meta }) => (
                <FormItem label="Channel" error={getFieldError(meta)}>
                  <SingleSelect
                    className="w-full"
                    placeholder="Select..."
                    value={input.value}
                    onChange={(distributionChannel: any) => {
                      input.onChange(distributionChannel)
                      setIsDistributionChannelSelected(!!distributionChannel)
                      formChange(
                        'distributionChannel',
                        distributionChannel.value
                      )
                      formChange('integrationCredentials', undefined)
                    }}
                    options={distributionChannels?.map((value) => ({
                      label: distributionChannelMap[value],
                      value,
                    }))}
                  />
                </FormItem>
              )}
            </Field>

            {isDistributionChannelSelected &&
              selectedDistributionChannel?.messageType && (
                <>
                  <Field<DistributionTypeValue>
                    name="distributionType"
                    validate={validateDistributionType}
                  >
                    {({ input, meta }) => (
                      <FormItem
                        label="Distribution type"
                        htmlFor="distributionType"
                        error={getFieldError(meta)}
                      >
                        <RadioGroup
                          data-cy="distribution-type"
                          data-testid="distribution-type"
                          value={input.value}
                          name="distribution-type"
                          options={selectedDistributionChannel?.messageType.map(
                            ({ value }) => {
                              return {
                                label: messageTypeMap[value],
                                value,
                              }
                            }
                          )}
                          onChange={(e) => {
                            input.onChange(e)
                            formChange('integrationCredentials', undefined)
                          }}
                        />
                      </FormItem>
                    )}
                  </Field>
                </>
              )}
            {isDistributionChannelSelected &&
              selectedDistributionChannel?.testingMethodology && (
                <>
                  <Field<TestingMethodValue>
                    name="testingMethod"
                    validate={validateTestingMethod}
                  >
                    {({ input, meta }) => (
                      <FormItem className="mb-6" error={getFieldError(meta)}>
                        <SingleSelect
                          data-cy="testing-method-select"
                          data-testid="testing-method-select"
                          className="max-w-xs"
                          label="Testing methodology"
                          name="testingMethod"
                          value={input.value}
                          options={
                            selectedDistributionChannel?.testingMethodologyOptions
                          }
                          onChange={(val) => {
                            val && input.onChange(val.value)
                            formChange('testingMethod', val?.value)
                            formChange('integrationCredentials', undefined)
                          }}
                        />
                      </FormItem>
                    )}
                  </Field>
                </>
              )}
            {testingMethod && (
              <>
                {selectedDistributionChannel?.testingMethodology?.[
                  testingMethod
                ]?.credentials?.map(({ name, label, type }) => (
                  <Field
                    key={name}
                    name={`integrationCredentials.${name}`}
                    validate={(val) => validateIntegrationCredentials(val)}
                  >
                    {({ input, meta }) => (
                      <FormItem
                        label={label}
                        htmlFor={name}
                        error={getFieldError(meta)}
                      >
                        {
                          {
                            text: (
                              <Input
                                data-cy={`${name}-input`}
                                data-testid={`${name}-input`}
                                className="max-w-xs"
                                type="text"
                                variant="default"
                                id={name}
                                name={name}
                                value={input.value || ''}
                                onChange={input.onChange}
                              />
                            ),
                            timezone: (
                              <TimeZoneSelect
                                data-cy={`${name}-select`}
                                data-testid={`${name}-select`}
                                value={input.value || ''}
                                onChange={input.onChange}
                              />
                            ),
                            password: (
                              <PasswordInput
                                data-cy={`${name}-input`}
                                data-testid={`${name}-input`}
                                className="max-w-xs"
                                id={name}
                                name={name}
                                value={input.value || ''}
                                onChange={input.onChange}
                              />
                            ),
                          }[type]
                        }
                      </FormItem>
                    )}
                  </Field>
                ))}
              </>
            )}
          </div>

          <div className="flex justify-end items-center mt-8 space-x-4">
            <Button
              data-cy="cancel-button"
              onClick={onClose}
              className="bg-gold-50 border-gold-500 border-1"
            >
              CANCEL
            </Button>
            <Button
              type="submit"
              data-cy="add-button"
              variant="primary"
              disabled={hasValidationErrors || (isEditing && !dirty)}
            >
              {isEditing ? 'Update' : 'Add'}
            </Button>
          </div>
        </form>
      </Widget>
    </div>
  )
}

export default CreateCepIntegrationWidget
